import { defineHierarchyChart } from '@rdrive/helper-chart-config';
import { STATUS, ITEM_TEMPLATES, CATEGORIES, issueTemplateShortCircuit } from './itemsHierarchy.js';

// Status-history item breakdown: category -> subcategory -> profile -> subitem -> status, for the
// Activity page's StackedHorizontalBarChart (@rdrive/plugin-chart-stacked-bar). Since
// IA_ABSORPTION_PLAN Phase 5 steps 5.2–5.4 a level table on `defineHierarchyChart`
// (@rdrive/helper-chart-config) — the generated queries equal the old hand-written ones
// (tooling/parity/hierarchy-chart/), and every filter value goes through escapeSql (rule 6).
//
// Every level reads `filteredStatusHistory` — the providers ignore the base's `filtered` flag, as
// they always did. The filters select the status_history_temporal key tuple (subItemId, validFrom,
// statusId). Scoping ('replaceSource'): every non-status filter becomes the drill scope, which
// REPLACES the FROM source of the terminal status filter (rather than adding a predicate).
//
// `currentLevel` is the page-owned ref bound with v-model:currentLevel — writing to it is how the
// IssueTemplate short-circuit (the profile level's `onSelect`) skips the subitem level.

const FORM_TEMPLATES = 'JOIN form_templates ft ON i.subItemTemplateId = ft.id';

export function useStatusHistoryCategoryChart({ chartName, filterContext, currentLevel, conn }) {
  return defineHierarchyChart({
    chartName,
    filterContext,
    conn,
    currentLevel,
    source: { table: 'filteredStatusHistory', alias: 'i' },
    status: { join: STATUS },
    filter: { table: 'status_history_temporal', keys: 'subItemId, validFrom, statusId', scope: 'replaceSource' },
    defaults: { value: 'window' },
    levels: [
      {
        level: 'category', titleKey: 'category',
        id: 'c.id', title: 'c.title', joins: [ITEM_TEMPLATES, CATEGORIES],
        filter: { joins: [ITEM_TEMPLATES], where: [['it.categoryId', 'id']] },
      },
      // A subcategory bar carries its template type (`it.type`) as parentId — the short-circuit reads it
      {
        level: 'subcategory', titleKey: 'subcategory',
        id: 'it.subcategoryId', parentId: 'it.type', title: 'it.subcategoryTitle', joins: [ITEM_TEMPLATES],
        groupBy: 'it.subcategoryId, it.type, it.subcategoryTitle, s.color, s.title, s.sort',
        orderBy: 'it.subcategoryTitle, s.sort, s.title ASC',
        filter: { joins: [ITEM_TEMPLATES], where: [['it.subcategoryID', 'id']] },
      },
      {
        level: 'profile', titleKey: 'profile',
        id: 'it.id', parentId: 'it.code', type: 'it."Type"', title: 'it.title', joins: [ITEM_TEMPLATES],
        groupBy: 'it.id, it.code, it."Type", it.title, s.title, s.color, s.sort',
        partitionBy: 'it.id, it.code, it."Type", it.title, s.color, s.sort',
        orderBy: 'it.title, s.sort, s.title ASC',
        onSelect: issueTemplateShortCircuit,
        filter: { where: [['i.profileId', 'id']] },
      },
      // The subitem rows carry the literal type 'ProcessTemplate', grouped by its alias `type`
      {
        level: 'subitem', titleKey: 'subitem',
        id: 'ft.code', parentId: 'ft.code', type: "'ProcessTemplate'", title: 'ft.title', joins: [FORM_TEMPLATES],
        groupBy: 'ft.code, type, ft.title, s.title, s.color, s.sort',
        partitionBy: 'ft.code, type, ft.title, s.color, s.sort',
        orderBy: 'ft.title, s.sort, s.title ASC',
        filter: { joins: [FORM_TEMPLATES], where: [['ft.code', 'id']] },
      },
      // The status filter qualifies its keys and joins the status table itself; it reads FROM the
      // drill scope when one is set
      {
        level: 'status', titleKey: 'status',
        id: 's.title', parentId: 'it.code', type: 'it."Type"', title: 's.title', joins: [STATUS, ITEM_TEMPLATES],
        groupBy: 's.title, it.code, it."Type", s.title, s.color', having: 'COUNT(*) > 0', orderBy: 's.title',
        filter: {
          keys: 'i.subItemId, i.validFrom, i.statusId',
          joins: ['JOIN status_templates s ON i.statusId = s.id'],
          where: [['s.title', 'id']],
          scoped: true,
        },
      },
    ],
  });
}
